import type { TweAdvisorProduct } from '../host/types';
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : typeof value === 'number' ? String(value) : undefined;
}
export function safeUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try { const url = new URL(value, window.location.href); return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined; } catch { return undefined; }
}
/** Accept only finite numeric money, not coercible nulls, booleans or containers. */
function moneyInPence(value: unknown): number | undefined {
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim()))) return undefined;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return undefined;
  // Decimal exponent shifting rounds to pennies without binary multiplication artefacts.
  const [coefficient, exponent = '0'] = String(amount).split('e');
  const pence = Math.round(Number(`${coefficient}e${Number(exponent) + 2}`));
  return Number.isSafeInteger(pence) ? pence : undefined;
}
/** TWE generic `price` is ex VAT. Offers require both prices on this product's data. */
export function adaptProduct(raw: Record<string, unknown>, currency = 'GBP'): TweAdvisorProduct {
  const productData = raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data as Record<string, unknown> : undefined;
  const data = productData || raw;
  const fullName = text(data.productDisplayName) || text(data.name) || text(raw.value) || '';
  const [name, ...secondary] = fullName.split(' / ');
  const regularPrice = data.salesPriceIncVat ?? data.salesPrice ?? data.price;
  const regularPence = moneyInPence(productData?.salesPriceIncVat);
  const discountedPence = moneyInPence(productData?.discountedSalesPrice);
  const isOffer = regularPence !== undefined && discountedPence !== undefined && discountedPence > 0 && discountedPence < regularPence;
  const fallbackPence = moneyInPence(regularPrice);
  const price = isOffer ? discountedPence / 100 : fallbackPence !== undefined ? fallbackPence / 100 : regularPrice;
  return {
    id: text(data.id) || text(raw.id) || '', name,
    secondaryName: secondary.join(' / ') || undefined,
    imageUrl: text(data.image_url),
    size: data.sizeInCL != null ? `${data.sizeInCL}cl` : text(data.size),
    strength: data.strengthInPC != null ? `${data.strengthInPC}%` : text(data.strength),
    price: typeof price === 'number' || typeof price === 'string' ? price : undefined,
    ...(isOffer && {saving: (regularPence - discountedPence) / 100}),
    currency: text(data.currency) || currency,
    inStock: typeof data.isAvailableToBuy === 'boolean' ? data.isAvailableToBuy && data.isOutOfStock !== true : typeof data.isOutOfStock === 'boolean' ? !data.isOutOfStock : undefined,
    variationId: text(data.variation_id), raw,
  };
}
export function priceLabel(product: TweAdvisorProduct) {
  if (product.price == null) return '';
  const pence = moneyInPence(product.price);
  if (pence === undefined) return String(product.price);
  const amount = pence / 100;
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: product.currency || 'GBP', minimumFractionDigits: Number.isInteger(amount) ? 0 : 2 }).format(amount); }
  catch { return String(product.price); }
}
